import { useTranslation } from '@/store/languageStore';
import {
  DocumentArrowUpIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  CloudArrowUpIcon,
  TableCellsIcon,
  ArrowsRightLeftIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { toTestId } from './types';
import type { UseImportsReturn } from './useImportsManagement';
import { HeaderCellsMapper } from './HeaderCellsMapper';

const getTypeColor = (type: string) => {
  switch (type) {
    case 'number': return 'text-blue-500';
    case 'date': return 'text-purple-500';
    case 'boolean': return 'text-green-500';
    default: return 'text-textTertiary';
  }
};

const formatSourceHealthStatus = (status: 'success' | 'failed' | null | undefined, t: (key: string) => string) => {
  if (status === 'success') return t('imports.sourceHealthSuccess') || 'Lyckad';
  if (status === 'failed') return t('imports.sourceHealthFailed') || 'Misslyckad';
  return t('imports.sourceHealthUnknown') || 'Okänd';
};

const getSourceStatusBadgeClasses = (status?: 'success' | 'failed' | null) => {
  if (status === 'success') return 'bg-green-500/15 text-green-700 dark:text-green-300 border border-green-500/30';
  if (status === 'failed') return 'bg-red-500/15 text-red-700 dark:text-red-300 border border-red-500/30';
  return 'bg-bgElevated text-textSecondary border border-border';
};

export const ImportForm = ({ state }: { state: UseImportsReturn }) => {
  const { t } = useTranslation();
  const {
    showWizard, setShowWizard, wizardStep, setWizardStep, editingId,
    importName, setImportName, importDescription, setImportDescription,
    selectedTable, setSelectedTable, allowedTables,
    importSources, importSourcesLoading, selectedSourceId, setSelectedSourceId,
    tableFields, filePreview, setFilePreview,
    sourcePreviewInfo, setSourcePreviewInfo,
    sourcePreviewLoading, sourcePreviewError,
    fieldMappings,
    uploading,
    selectedPreset, setSelectedPreset,
    schemaValidationErrors,
    decimalSeparator, setDecimalSeparator,
    hasHeader, setHasHeader,
    delimiter, setDelimiter,
    skipRows, setSkipRows,
    headerCells, upsertHeaderCell, removeHeaderCell,
    filenameIncludePatterns, setFilenameIncludePatterns,
    sheetName, setSheetName,
    sheetIndex, setSheetIndex,
    duplicateAction, setDuplicateAction,
    errorAction, setErrorAction,
    scheduleType, setScheduleType,
    scheduleTime, setScheduleTime,
    scheduleDayOfWeek, setScheduleDayOfWeek,
    scheduleDayOfMonth, setScheduleDayOfMonth,
    scheduleCron, setScheduleCron,
    fileSourcePath, setFileSourcePath,
    stableFileCheckIntervalMs, setStableFileCheckIntervalMs,
    stableFileStableForMs, setStableFileStableForMs,
    stableFileTimeoutMs, setStableFileTimeoutMs,
    retryMaxRetries, setRetryMaxRetries,
    retryInitialDelayMs, setRetryInitialDelayMs,
    retryMaxDelayMs, setRetryMaxDelayMs,
    retryBackoffMultiplier, setRetryBackoffMultiplier,
    handleDrop, handleFileSelect,
    loadPreviewFromSelectedSource,
    addMapping, removeMapping,
    addTransformStep, removeTransformStep, moveTransformStep,
    updateTransformType, getTransformParamsText,
    handleTransformParamsChange, handleTransformParamsBlur,
    saveDefinition, applyPreset, clearPreset,
    selectedSource, isFuelDisallowedTarget, presetEntries,
    getLivePreviewValue,
  } = state;

  if (!showWizard) return null;

  const renderSourceHealth = () => {
    if (!selectedSource) return null;
    const status = selectedSource.last_fetch_status ?? null;
    const statusClass = status === 'success' ? 'text-green-600 dark:text-green-400'
      : status === 'failed' ? 'text-red-600 dark:text-red-400' : 'text-textSecondary';

    return (
      <div className="rounded-lg border border-border bg-bgBase p-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-textSecondary">{t('imports.lastFetchAt') || 'Senaste hämtning'}</div>
            <div className="text-textDefault font-medium">
              {selectedSource.last_fetch_at ? new Date(selectedSource.last_fetch_at).toLocaleString('sv-SE') : (t('imports.notFetchedYet') || 'Aldrig')}
            </div>
          </div>
          <div>
            <div className="text-textSecondary">{t('imports.lastFetchStatus') || 'Hälsostatus'}</div>
            <div className={`font-medium ${statusClass}`}>{formatSourceHealthStatus(selectedSource.last_fetch_status, t)}</div>
          </div>
          <div>
            <div className="text-textSecondary">{t('imports.filesInSource') || 'Filer i källan'}</div>
            <div className="text-textDefault font-medium">
              {selectedSource.last_fetch_files_count != null ? selectedSource.last_fetch_files_count : '–'}
            </div>
          </div>
        </div>
        {selectedSource.last_fetch_status === 'failed' && selectedSource.last_fetch_error && (
          <div className="mt-3 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            <span className="font-medium">{t('imports.sourceSyncError') || 'Sync-fel'}:</span> {selectedSource.last_fetch_error}
          </div>
        )}
      </div>
    );
  };

  const renderSourceStatusList = () => {
    if (importSources.length === 0) return null;
    return (
      <div className="rounded-lg border border-border bg-bgBase p-3 space-y-2">
        <div className="text-sm font-medium text-textSecondary">{t('imports.sourcesStatusTitle') || 'Källstatus'}</div>
        <div className="space-y-2">
          {importSources.map((source) => (
            <div key={source.id} className="rounded-md border border-border px-3 py-2">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm text-textDefault">{source.name} ({source.type})</div>
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getSourceStatusBadgeClasses(source.last_fetch_status)}`}>
                  {formatSourceHealthStatus(source.last_fetch_status, t)}
                </span>
              </div>
              {source.last_fetch_status === 'failed' && source.last_fetch_error && (
                <div className="mt-1 text-xs text-red-600 dark:text-red-400">
                  {(t('imports.sourceSyncError') || 'Sync-fel')}: {source.last_fetch_error}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-textDefault">{t('imports.step1Title')}</h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-textSecondary mb-1">{t('imports.importNameLabel')}</label>
          <input type="text" value={importName} onChange={(e) => setImportName(e.target.value)}
            className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault"
            placeholder={t('importsManagement.namePlaceholder')} data-testid="import-name-input" />
        </div>
        <div>
          <label className="block text-sm font-medium text-textSecondary mb-1">{t('imports.descriptionLabel')}</label>
          <textarea value={importDescription} onChange={(e) => setImportDescription(e.target.value)}
            className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault"
            rows={2} placeholder={t('imports.descriptionPlaceholder')} data-testid="import-description-input" />
        </div>
        <div>
          <label className="block text-sm font-medium text-textSecondary mb-2">{t('imports.presetsLabel')}</label>
          <div className="grid grid-cols-3 gap-2">
            {presetEntries.map((entry) => (
              <button key={entry.key} type="button" onClick={() => applyPreset(entry.key)}
                className={`px-4 py-2 rounded-lg border transition-colors ${selectedPreset === entry.key
                  ? 'bg-primary/10 border-primary text-primary'
                  : 'bg-bgBase border-border text-textDefault hover:bg-bgElevated'}`}
                data-testid={`preset-${entry.key}`}
              >
                {entry.label}
              </button>
            ))}
          </div>
          {selectedPreset && (
            <button type="button" onClick={() => clearPreset()} className="mt-2 text-sm text-primary hover:underline" data-testid="preset-clear">
              {t('imports.clearPreset')}
            </button>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-textSecondary mb-1">{t('imports.targetTableLabel')}</label>
          <select value={selectedTable}
            onChange={(e) => { setSelectedTable(e.target.value); if (e.target.value !== 'fuel_transactions') setSelectedPreset(''); }}
            className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault" data-testid="target-table-select"
          >
            <option value="">{t('importsManagement.selectTable')}</option>
            {allowedTables.map((table) => (
              <option key={table.tableName} value={table.tableName}>{table.displayName} ({table.tableName})</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-textSecondary mb-1">{t('imports.importSource') || 'Import Source'}</label>
          <select value={selectedSourceId} onChange={(e) => setSelectedSourceId(e.target.value)}
            className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault" data-testid="import-source-select"
          >
            <option value="">{t('imports.importSourceNone') || 'Ingen källa'}</option>
            {importSources.map((source) => (
              <option key={source.id} value={source.id}>{source.name} ({source.type})</option>
            ))}
          </select>
          {importSourcesLoading && <div className="mt-1 text-xs text-textSecondary">{t('common.loading') || 'Laddar...'}</div>}
        </div>
        {selectedSource && renderSourceHealth()}
        {renderSourceStatusList()}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-textSecondary mb-1">{t('imports.decimalSeparatorLabel')}</label>
            <select value={decimalSeparator} onChange={(e) => setDecimalSeparator(e.target.value as ',' | '.')}
              className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault">
              <option value=",">{t('imports.decimalComma')}</option>
              <option value=".">{t('imports.decimalDot')}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-textSecondary mb-1">{t('imports.csvSeparatorLabel')}</label>
            <select value={delimiter} onChange={(e) => setDelimiter(e.target.value)}
              className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault">
              <option value=";">{t('imports.csvSemicolon')}</option>
              <option value=",">{t('imports.csvComma')}</option>
              <option value="\t">{t('imports.csvTab')}</option>
              <option value="|">{t('imports.csvPipe')}</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={hasHeader} onChange={(e) => setHasHeader(e.target.checked)} className="rounded border-border" />
              <span className="text-sm text-textSecondary">{t('importsManagement.firstRowIsHeader')}</span>
            </label>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-textSecondary mb-1">{t('imports.excelSheetNameLabel')}</label>
            <input type="text" value={sheetName} onChange={(e) => setSheetName(e.target.value)}
              className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault"
              placeholder={t('imports.excelSheetNamePlaceholder')} data-testid="excel-sheet-name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-textSecondary mb-1">{t('imports.excelSheetIndexLabel')}</label>
            <input type="number" min={0} value={sheetIndex} onChange={(e) => setSheetIndex(e.target.value)}
              className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault"
              placeholder="0" data-testid="excel-sheet-index" />
          </div>
          <div>
            <label className="block text-sm font-medium text-textSecondary mb-1">Hoppa över rader (före rubrik)</label>
            <input type="number" min={0} value={skipRows}
              onChange={(e) => setSkipRows(Math.max(0, Number(e.target.value) || 0))}
              className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault"
              placeholder="0" data-testid="skip-rows" />
          </div>
          <div className="col-span-3 text-xs text-textSecondary">
            {t('imports.excelSheetIndexHint')} Hoppa över rader hoppar förbi t.ex. ett fakturahuvud innan rubrikraden (Qstar: 6).
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-textDefault">{t('imports.step2Title')}</h3>
      <div className="space-y-2">
        <div>
          <label className="block text-sm font-medium text-textSecondary mb-1">{t('imports.importSource') || 'Import Source'}</label>
          <select value={selectedSourceId} onChange={(e) => setSelectedSourceId(e.target.value)}
            className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault" data-testid="import-source-select-step-2">
            <option value="">{t('imports.importSourceNone') || 'Ingen källa'}</option>
            {importSources.map((source) => (
              <option key={source.id} value={source.id}>{source.name} ({source.type})</option>
            ))}
          </select>
        </div>
        {sourcePreviewLoading && selectedSourceId && (
          <div className="text-sm text-textSecondary">{t('imports.sourcePreviewLoading') || 'Laddar senaste fil från källa...'}</div>
        )}
        {selectedSource && (
          <>
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm text-textSecondary">
                {t('imports.selectedSource') || 'Vald källa'}: <span className="font-medium text-textDefault">{selectedSource.name} ({selectedSource.type})</span>
              </div>
              <button type="button" onClick={() => loadPreviewFromSelectedSource(selectedSourceId)}
                disabled={sourcePreviewLoading}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-border rounded-md text-textSecondary hover:text-textDefault hover:bg-bgBase disabled:opacity-50"
                data-testid="reload-source-preview-button">
                <ArrowPathIcon className={`w-3.5 h-3.5 ${sourcePreviewLoading ? 'animate-spin' : ''}`} />
                {t('imports.sourcePreviewReload') || 'Ladda om från källa'}
              </button>
            </div>
            {renderSourceHealth()}
          </>
        )}
        {sourcePreviewError && (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">{sourcePreviewError}</div>
        )}
        {renderSourceStatusList()}
      </div>

      <div onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}
        className="border-2 border-dashed border-border rounded-xl p-8 text-center hover:border-primary transition-colors">
        {uploading ? (
          <div className="flex flex-col items-center gap-3">
            <ArrowPathIcon className="w-10 h-10 text-primary animate-spin" />
            <span className="text-textSecondary">{t('imports.importingFile')}</span>
          </div>
        ) : filePreview ? (
          <div className="flex flex-col items-center gap-3">
            <CheckCircleIcon className="w-10 h-10 text-green-500" />
            <span className="text-textDefault font-medium">{filePreview.filename}</span>
            <span className="text-textSecondary text-sm">{filePreview.totalRows} rader · {filePreview.columns.length} kol.</span>
            {sourcePreviewInfo && (
              <span className="text-xs text-textSecondary">
                {t('imports.sourcePreviewLoaded') || 'Mallfil laddad från källa'}: {sourcePreviewInfo.originalFilename}
                {sourcePreviewInfo.fileDate ? ` (${new Date(sourcePreviewInfo.fileDate).toLocaleString('sv-SE')})` : ''}
              </span>
            )}
            <button onClick={() => { setFilePreview(null); setSourcePreviewInfo(null); }} className="text-sm text-primary hover:underline">{t('imports.changeFile')}</button>
          </div>
        ) : (
          <label className="cursor-pointer flex flex-col items-center gap-3">
            <CloudArrowUpIcon className="w-12 h-12 text-textSecondary" />
            <span className="text-textDefault">{t('importsManagement.dragFileHere')}</span>
            <span className="text-textSecondary text-sm">{t('imports.supportedFormats')}</span>
            <input type="file" onChange={handleFileSelect} accept=".xlsx,.xls,.csv,.txt,.json,.xml,.dat" className="hidden" data-testid="import-file-input" />
          </label>
        )}
      </div>

      {filePreview && skipRows > 0 && (!filePreview.preambleRows || filePreview.preambleRows.length === 0) && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
          {t('imports.headerCellsReloadHint')}
        </div>
      )}

      {filePreview?.preambleRows && filePreview.preambleRows.length > 0 && (
        <HeaderCellsMapper
          preambleRows={filePreview.preambleRows}
          headerCells={headerCells}
          tableFields={tableFields}
          onUpsert={upsertHeaderCell}
          onRemove={removeHeaderCell}
        />
      )}

      {filePreview && filePreview.columns.length > 0 && (
        <div className="bg-bgBase rounded-lg border border-border p-4">
          <h4 className="font-medium text-textDefault mb-3">{t('imports.identifiedColumns')}</h4>
          <div className="flex flex-wrap gap-2">
            {filePreview.columns.map((col) => (
              <div key={col.index} className="px-3 py-1 bg-bgElevated rounded-full text-sm flex items-center gap-2">
                <span className="text-textDefault">{col.name}</span>
                <span className={`text-xs ${getTypeColor(col.detectedType)}`}>({col.detectedType})</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-textDefault">{t('imports.step3Title')}</h3>
      {schemaValidationErrors.length > 0 && (
        <div className="rounded-lg border border-red-500/50 bg-red-500/10 p-4" role="alert" data-testid="schema-validation-errors">
          <h4 className="font-medium text-red-600 dark:text-red-400 mb-2">{t('imports.schemaMappingError')}</h4>
          <ul className="list-disc list-inside space-y-1 text-sm text-red-700 dark:text-red-300">
            {schemaValidationErrors.map((err, i) => (
              <li key={i}>{err.targetField && <strong>{err.targetField}:</strong>} {err.message}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-2 gap-6">
        <div className="bg-bgBase rounded-lg border border-border p-4">
          <h4 className="font-medium text-textDefault mb-3 flex items-center gap-2">
            <DocumentArrowUpIcon className="w-5 h-5" />{t('imports.sourcePanelTitle')}
          </h4>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {filePreview?.columns.map((col) => (
              <div key={col.index} draggable onDragStart={(e) => e.dataTransfer.setData('sourceColumn', col.name)}
                className="px-3 py-2 bg-bgElevated rounded-lg cursor-move hover:bg-primary/10 transition-colors"
                data-testid={`source-col-${toTestId(col.name)}`}>
                <div className="flex justify-between items-center">
                  <span className="text-textDefault">{col.name}</span>
                  <span className={`text-xs ${getTypeColor(col.detectedType)}`}>{col.detectedType}</span>
                </div>
                {col.sampleValues[0] && <div className="text-xs text-textSecondary mt-1 truncate">{t('imports.examplePrefix')}: {col.sampleValues[0]}</div>}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-bgBase rounded-lg border border-border p-4">
          <h4 className="font-medium text-textDefault mb-3 flex items-center gap-2">
            <TableCellsIcon className="w-5 h-5" />{t('imports.targetPanelTitle')} ({selectedTable})
          </h4>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {tableFields
              .filter((field) => !isFuelDisallowedTarget(field.name))
              .map((field) => {
                const mapping = fieldMappings.find((m) => m.targetField === field.name);
                return (
                  <div key={field.name}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      const sourceColumn = e.dataTransfer.getData('sourceColumn');
                      if (sourceColumn && !mapping) addMapping(sourceColumn, field.name);
                    }}
                    className={`px-3 py-2 rounded-lg transition-colors ${mapping ? 'bg-green-500/10 border border-green-500/30' : 'bg-bgElevated hover:bg-primary/10'}`}
                    data-testid={`target-field-${toTestId(field.name)}`}>
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="text-textDefault">{field.name}</span>
                        {field.isPrimaryKey && <span className="ml-2 text-xs text-yellow-500">PK</span>}
                        {!field.nullable && !field.isPrimaryKey && <span className="ml-2 text-xs text-red-500">*</span>}
                      </div>
                      <span className="text-xs text-textSecondary">{field.type}</span>
                    </div>
                    {mapping && (
                      <div className="flex items-center justify-between mt-1">
                        <div className="flex items-center gap-1 text-xs text-green-600">
                          <ArrowsRightLeftIcon className="w-3 h-3" />{mapping.sourceColumns.join(', ')}
                        </div>
                        <button onClick={() => removeMapping(mapping.id)} className="text-red-500 hover:text-red-600">
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {fieldMappings.length > 0 && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
          <span className="text-green-600 font-medium">{t('imports.fieldsMappedCount').replace('{n}', String(fieldMappings.length))}</span>
        </div>
      )}
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-textDefault">{t('imports.step4Title')}</h3>
      {sourcePreviewLoading && (
        <div className="text-sm text-textSecondary">{t('imports.sourcePreviewLoading') || 'Laddar senaste fil från källa...'}</div>
      )}
      {!sourcePreviewLoading && !filePreview?.columns?.length && selectedSourceId && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
          {t('imports.livePreviewNeedsSample') || 'Ingen mallfil laddad – gå tillbaka till steg 2 och ladda om från källa för att se förhandsvisning.'}
        </div>
      )}
      {fieldMappings.length === 0 ? (
        <div className="text-textSecondary">{t('imports.addMappingInStep3')}</div>
      ) : (
        <div className="space-y-4" data-testid="transform-section">
          {fieldMappings.map((mapping) => (
            <div key={mapping.id} className="bg-bgBase rounded-lg border border-border p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-textDefault font-medium">{mapping.targetField}</div>
                  <div className="text-xs text-textSecondary">{t('imports.sourceLabel')}: {mapping.sourceColumns.join(', ')}</div>
                </div>
                <button type="button" onClick={() => addTransformStep(mapping.id)}
                  className="px-3 py-1 text-sm bg-bgElevated border border-border rounded-lg hover:bg-primary/10"
                  data-testid={`add-transform-${toTestId(mapping.targetField)}`}>
                  {t('imports.addTransform')}
                </button>
              </div>

              {(mapping.transforms || []).length === 0 ? (
                <div className="text-sm text-textSecondary">{t('imports.noTransformsAdded')}</div>
              ) : (
                <div className="space-y-3">
                  {(mapping.transforms || []).map((step, index) => (
                    <div key={`${mapping.id}-${index}`} className="grid grid-cols-12 gap-3 items-start">
                      <div className="col-span-3">
                        <label className="block text-xs text-textSecondary mb-1">{t('imports.typeLabel')}</label>
                        <select value={step.type} onChange={(e) => updateTransformType(mapping.id, index, e.target.value)}
                          className="w-full px-2 py-1 bg-bgElevated border border-border rounded-lg text-textDefault text-sm">
                          {['trim', 'uppercase', 'lowercase', 'substring', 'regex_replace', 'concat', 'decimal_convert', 'parse_number', 'multiply', 'add', 'subtract_column', 'divide', 'round', 'date_parse', 'date_format', 'default_value'].map((type) => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-7">
                        <label className="block text-xs text-textSecondary mb-1">{t('imports.paramsLabel')}</label>
                        <textarea
                          value={getTransformParamsText(mapping.id, index, step.params)}
                          onChange={(e) => handleTransformParamsChange(mapping.id, index, e.target.value)}
                          onBlur={() => handleTransformParamsBlur(mapping.id, index)}
                          className="w-full px-2 py-1 bg-bgElevated border border-border rounded-lg text-textDefault text-xs"
                          rows={3} />
                      </div>
                      <div className="col-span-2 flex gap-2 pt-6">
                        <button
                          type="button"
                          onClick={() => moveTransformStep(mapping.id, index, -1)}
                          className="p-1.5 text-xs bg-bgElevated border border-border rounded disabled:opacity-40"
                          title={t('imports.moveUp')}
                          disabled={index === 0}
                          aria-label={t('imports.moveUp')}
                        >
                          <ChevronUpIcon className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveTransformStep(mapping.id, index, 1)}
                          className="p-1.5 text-xs bg-bgElevated border border-border rounded disabled:opacity-40"
                          title={t('imports.moveDown')}
                          disabled={index === (mapping.transforms || []).length - 1}
                          aria-label={t('imports.moveDown')}
                        >
                          <ChevronDownIcon className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeTransformStep(mapping.id, index)}
                          className="p-1.5 text-xs text-red-500 bg-bgElevated border border-border rounded"
                          title={t('imports.remove')}
                          aria-label={t('imports.remove')}
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-2 text-xs text-textSecondary">
                <div className="flex justify-between">
                  <span>{t('imports.livePreview')}</span>
                  <span className="text-textDefault font-medium">
                    <span data-testid={`transform-preview-${toTestId(mapping.targetField)}`}>{String(getLivePreviewValue(mapping))}</span>
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-textDefault">{t('imports.step5Title')}</h3>

      <div className="border border-border rounded-lg p-4 bg-bgBase/50 space-y-3" data-testid="filename-include-patterns">
        <div>
          <h4 className="font-medium text-textDefault">{t('imports.filenameFilterTitle')}</h4>
          <p className="text-xs text-textSecondary mt-1">{t('imports.filenameFilterHint')}</p>
        </div>
        <div className="space-y-2">
          {filenameIncludePatterns.map((pattern, index) => (
            <div key={`fn-pat-${index}`} className="flex gap-2 items-center">
              <input
                type="text"
                value={pattern}
                onChange={(e) => {
                  const next = [...filenameIncludePatterns];
                  next[index] = e.target.value;
                  setFilenameIncludePatterns(next);
                }}
                placeholder="*K05* eller *Fordon* eller ^\\d"
                className="flex-1 px-3 py-2 bg-bgElevated border border-border rounded-lg text-textDefault text-sm"
                data-testid={`filename-pattern-${index}`}
              />
              <button
                type="button"
                onClick={() => setFilenameIncludePatterns(filenameIncludePatterns.filter((_, i) => i !== index))}
                className="p-2 text-red-500 border border-border rounded-lg bg-bgElevated"
                title={t('imports.remove')}
                aria-label={t('imports.remove')}
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setFilenameIncludePatterns([...filenameIncludePatterns, ''])}
          className="text-sm text-primary hover:underline"
          data-testid="filename-pattern-add"
        >
          {t('imports.filenameFilterAdd')}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-textSecondary mb-1">{t('imports.duplicateActionLabel')}</label>
          <select value={duplicateAction} onChange={(e) => setDuplicateAction(e.target.value as any)}
            className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault" data-testid="duplicate-action-select">
            <option value="skip">{t('importsManagement.skipKeepExisting')}</option>
            <option value="update">{t('imports.updateExisting')}</option>
            <option value="upsert">UPSERT (insert + update)</option>
            <option value="replace">{t('importsManagement.replaceRow')}</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-textSecondary mb-1">{t('imports.errorActionLabel')}</label>
          <select value={errorAction} onChange={(e) => setErrorAction(e.target.value as 'skip' | 'abort')}
            className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault">
            <option value="skip">{t('imports.skipErroneousRows')}</option>
            <option value="abort">{t('imports.abortEntireImport')}</option>
          </select>
        </div>
      </div>

      <div className="space-y-4">
        <h4 className="font-medium text-textDefault">{t('imports.scheduleLabel')}</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-textSecondary mb-1">{t('imports.scheduleExecution')}</label>
            <select value={scheduleType} onChange={(e) => setScheduleType(e.target.value as any)}
              className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault">
              <option value="manual">{t('imports.scheduleManual')}</option>
              <option value="daily">{t('imports.scheduleDaily')}</option>
              <option value="weekly">{t('imports.scheduleWeekly')}</option>
              <option value="monthly">{t('imports.scheduleMonthly')}</option>
            </select>
          </div>
          {scheduleType !== 'manual' && (
            <>
              <div>
                <label className="block text-sm font-medium text-textSecondary mb-1">{t('imports.scheduleTimeLabel')}</label>
                <input type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)}
                  className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault" />
              </div>
              {scheduleType === 'weekly' && (
                <div>
                  <label className="block text-sm font-medium text-textSecondary mb-1">{t('imports.scheduleDayOfWeek')}</label>
                  <input type="number" min={0} max={6} value={scheduleDayOfWeek}
                    onChange={(e) => setScheduleDayOfWeek(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault" />
                </div>
              )}
              {scheduleType === 'monthly' && (
                <div>
                  <label className="block text-sm font-medium text-textSecondary mb-1">{t('imports.scheduleDayOfMonth')}</label>
                  <input type="number" min={1} max={31} value={scheduleDayOfMonth}
                    onChange={(e) => setScheduleDayOfMonth(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault" />
                </div>
              )}
            </>
          )}
        </div>
        {scheduleType !== 'manual' && (
          <>
            <div>
              <label className="block text-sm font-medium text-textSecondary mb-1">{t('imports.scheduleCronLabel')}</label>
              <input type="text" value={scheduleCron} onChange={(e) => setScheduleCron(e.target.value)}
                placeholder="0 6 * * *" className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault" />
            </div>
            <div>
              <label className="block text-sm font-medium text-textSecondary mb-1">{t('imports.fileSourcePathLabel')}</label>
              <input type="text" value={fileSourcePath} onChange={(e) => setFileSourcePath(e.target.value)}
                placeholder="/var/imports/fuel_*.dat" className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault" />
            </div>
            <div className="grid grid-cols-3 gap-4 border border-border rounded-lg p-4 bg-bgBase/50">
              <span className="col-span-3 text-sm font-medium text-textSecondary">{t('imports.stableFileTitle')}</span>
              <div>
                <label className="block text-xs text-textSecondary mb-1">{t('imports.stableCheckInterval')}</label>
                <input type="number" min={500} value={stableFileCheckIntervalMs}
                  onChange={(e) => setStableFileCheckIntervalMs(Number(e.target.value))}
                  className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault" />
              </div>
              <div>
                <label className="block text-xs text-textSecondary mb-1">{t('imports.stableStableFor')}</label>
                <input type="number" min={1000} value={stableFileStableForMs}
                  onChange={(e) => setStableFileStableForMs(Number(e.target.value))}
                  className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault" />
              </div>
              <div>
                <label className="block text-xs text-textSecondary mb-1">{t('imports.stableTimeout')}</label>
                <input type="number" min={0} value={stableFileTimeoutMs}
                  onChange={(e) => setStableFileTimeoutMs(Number(e.target.value))}
                  className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault" />
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 border border-border rounded-lg p-4 bg-bgBase/50">
              <span className="col-span-2 md:col-span-4 text-sm font-medium text-textSecondary">{t('imports.retryTitle')}</span>
              <div>
                <label className="block text-xs text-textSecondary mb-1">{t('imports.retryMaxRetries')}</label>
                <input type="number" min={1} value={retryMaxRetries}
                  onChange={(e) => setRetryMaxRetries(Number(e.target.value))}
                  className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault" />
              </div>
              <div>
                <label className="block text-xs text-textSecondary mb-1">{t('imports.retryInitialDelay')}</label>
                <input type="number" min={1000} value={retryInitialDelayMs}
                  onChange={(e) => setRetryInitialDelayMs(Number(e.target.value))}
                  className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault" />
              </div>
              <div>
                <label className="block text-xs text-textSecondary mb-1">{t('imports.retryMaxDelay')}</label>
                <input type="number" min={1000} value={retryMaxDelayMs}
                  onChange={(e) => setRetryMaxDelayMs(Number(e.target.value))}
                  className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault" />
              </div>
              <div>
                <label className="block text-xs text-textSecondary mb-1">{t('imports.retryBackoff')}</label>
                <input type="number" min={1} step={0.5} value={retryBackoffMultiplier}
                  onChange={(e) => setRetryBackoffMultiplier(Number(e.target.value))}
                  className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault" />
              </div>
            </div>
          </>
        )}
      </div>

      <div className="bg-bgBase rounded-lg border border-border p-4">
        <h4 className="font-medium text-textDefault mb-3">{t('imports.summaryTitle')}</h4>
        <dl className="grid grid-cols-2 gap-4 text-sm">
          <div><dt className="text-textSecondary">{t('imports.summaryName')}</dt><dd className="text-textDefault font-medium">{importName}</dd></div>
          <div><dt className="text-textSecondary">{t('imports.summaryTargetTable')}</dt><dd className="text-textDefault font-medium">{selectedTable}</dd></div>
          <div><dt className="text-textSecondary">{t('imports.summaryMappedFields')}</dt><dd className="text-textDefault font-medium">{fieldMappings.length}</dd></div>
          <div><dt className="text-textSecondary">{t('imports.summaryFileFormat')}</dt><dd className="text-textDefault font-medium">{filePreview?.format || 'N/A'}</dd></div>
          <div>
            <dt className="text-textSecondary">{t('imports.summarySchedule')}</dt>
            <dd className="text-textDefault font-medium">
              {scheduleType === 'manual' ? t('imports.scheduleManual')
                : `${scheduleType === 'daily' ? t('imports.scheduleDaily') : scheduleType === 'weekly' ? t('imports.scheduleWeekly') : t('imports.scheduleMonthly')} ${scheduleTime}${fileSourcePath ? ` · ${fileSourcePath}` : ''}`}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );

  const renderWizardStep = () => {
    switch (wizardStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      default: return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="import-wizard">
      <div className="bg-bgElevated rounded-xl border border-border w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-border flex justify-between items-center">
          <h2 className="text-xl font-semibold text-textDefault">
            {editingId ? t('imports.wizardTitleEdit') : t('imports.wizardTitleCreate')}
          </h2>
          <button
            onClick={() => setShowWizard(false)}
            className="text-textSecondary hover:text-textDefault"
            data-testid="wizard-close-button"
            aria-label={t('common.close') || 'Stäng'}
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 border-b border-border">
          <div className="flex items-center justify-between">
            {[1, 2, 3, 4, 5].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${wizardStep >= step ? 'bg-primary text-white' : 'bg-bgBase text-textSecondary'}`}>
                  {step}
                </div>
                {step < 5 && <div className={`w-24 h-1 mx-2 ${wizardStep > step ? 'bg-primary' : 'bg-bgBase'}`} />}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-xs text-textSecondary">
            <span>{t('imports.wizardStepBasicInfo')}</span><span>{t('imports.wizardStepLoadFile')}</span><span>{t('imports.wizardStepMapFields')}</span><span>{t('imports.wizardStepTransforms')}</span><span>{t('imports.wizardStepSettings')}</span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">{renderWizardStep()}</div>

        <div className="px-6 py-4 border-t border-border flex justify-between">
          <button
            onClick={() => { if (wizardStep === 1) setShowWizard(false); else setWizardStep(wizardStep - 1); }}
            className="px-4 py-2 text-textSecondary hover:text-textDefault" data-testid="wizard-back-button">
            {wizardStep === 1 ? t('imports.wizardCancel') : t('imports.wizardBack')}
          </button>
          <button
            onClick={() => { if (wizardStep === 5) saveDefinition(); else setWizardStep(wizardStep + 1); }}
            disabled={
              (wizardStep === 1 && (!importName || !selectedTable)) ||
              (wizardStep === 2 && !filePreview) ||
              (wizardStep === 3 && fieldMappings.length === 0)
            }
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="wizard-next-button">
            {wizardStep === 5 ? t('imports.wizardSave') : t('imports.wizardNext')}
          </button>
        </div>
      </div>
    </div>
  );
};
